import { useMemo, useRef, useState } from 'react';
import sortBy from 'lodash/sortBy';
import { useApolloClient } from '@apollo/client';

import { CONTENT_IMAGE } from 'global/constants';
import {
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { FileDropperContextProvider } from 'lib/form/file-dropper/file-dropper.context';
import { SlideshowSlide } from './slideshow.constants';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { useDeleteComponent } from 'approot/activity-builder/activity-builder.hooks';
import { useSortableScrollManager } from 'approot/shared/sortable/sortable.hooks';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { SORTABLE_CARD } from 'approot/shared/sortable/sortable.constants';
import SlideshowCreateImage from './slideshow-create-image';
import { PStyled } from 'approot/shared/styles/typography.style';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import {
  Sortable,
  SortableHeader,
  SortableContent,
  SortableItem,
} from 'approot/shared/sortable/sortable';
import { SecondaryButtonIcon } from 'approot/shared/styles/buttons/secondary-button.style';
import { removeImageFromSlideShowInCache } from './slideshow.utils';
import Spinner from 'approot/shared/spinner/spinner';
import IconTrash from 'approot/shared/icons/icon-trash';
import { getReorderSort } from 'approot/shared/sortable/sortable.utils';
import ComponentImageField from '../shared/component-image-field/component-image-field';
import ComponentImageModal from '../shared/component-image-modal/component-image-modal';
import { useFindComponentDimensions } from '../component.hooks';
import { removeTypenameFromObject } from 'approot/shared/logic/remove-typename';
import Switch from 'approot/shared/switch/switch';
import { ComponentSlideshow } from '../component.constants';
import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';

const SlideshowSettings = ({
  id,
  sectionId,
  contentId,
  slideshowImages,
  properties,
}: ComponentSlideshow & {
  contentId: number;
}) => {
  const client = useApolloClient();
  const ref = useRef<HTMLElement | null>(null);
  const [deleteItem, setDeleteItem] = useState<string>();
  const [editItemId, setEditItemId] = useState<string>();

  const { elementRef } = useFindComponentDimensions(id);
  const { closeDrawer } = useActivityBuilderContext();
  const { showError } = useSnackbar();

  const sortedSlides = useMemo(
    () => sortBy(slideshowImages, 'sort'),
    [slideshowImages]
  );

  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const { loading, updateComponentProperties } =
    useBatchComponentUpdateContext();

  const { deleteComponent, loading: deleteLoading } = useDeleteComponent({
    onError: () => {
      setDeleteItem(undefined);
      showError('There was an error deleting the image. Please try again');
    },
  });

  const selectedItem = useMemo(
    () => sortedSlides.find(s => s.id === editItemId),
    [editItemId, sortedSlides]
  );

  useSortableScrollManager(ref, [SORTABLE_CARD]);

  return (
    <ActivityBuilderDrawerWrapper>
      {isVisible && selectedItem && (
        <ComponentImageModal
          isImageRequired
          loading={loading}
          containerDimensions={{
            width: elementRef?.offsetWidth || 0,
            height: elementRef?.offsetHeight || 0,
          }}
          image={selectedItem.properties.image || undefined}
          sourceUrl={selectedItem.properties.sourceUrl}
          altText={selectedItem.properties.altText}
          cropData={
            selectedItem.properties.cropData
              ? removeTypenameFromObject(selectedItem.properties.cropData)
              : null
          }
          closeModal={closeModal}
          onUpdateData={data =>
            updateComponentProperties({
              input: {
                sectionId,
                componentId: selectedItem.id,
                componentData: {
                  properties: {
                    sourceUrl: data.sourceUrl,
                    cropData: data.cropData,
                    altText: data.altText,
                    image: data.image
                      ? {
                          imageType: CONTENT_IMAGE,
                          key: data.image.key,
                          fileName: data.image.fileName,
                          data: {
                            flip: data.image.data?.flip || null,
                          },
                        }
                      : null,
                  },
                },
              },
              typename: 'ActivitiesSlideshowComponent',
              isSaveImmediate: true,
              onCompleted: () => closeModal(),
            })
          }
        />
      )}
      <ActivityBuilderDrawerHeader title="Slideshow" onClose={closeDrawer} />
      <ActivityBuilderDrawerContent ref={ref}>
        <FileDropperContextProvider>
          <SlideshowCreateImage
            key={sortedSlides.map(s => s.id).join(',')}
            slideshowComponentId={id}
            sectionId={sectionId}
            sortedSlides={sortedSlides}
            contentId={contentId}
          />
        </FileDropperContextProvider>

        <SettingsGroup>
          <SettingsLabel>Image Caption</SettingsLabel>
          <Switch
            data-dropdown-item="true"
            ariaLabel={`Click to ${
              properties.showSlideCaptionOnLesson ? 'hide' : 'show'
            } caption`}
            state={!!properties.showSlideCaptionOnLesson}
            onClick={() => {
              updateComponentProperties({
                input: {
                  sectionId,
                  componentId: id,
                  componentData: {
                    properties: {
                      dimensionData: properties.dimensionData,
                      showCaptionOnLesson: !properties.showSlideCaptionOnLesson,
                    },
                  },
                },
                properties: {
                  ...properties,
                  // @ts-ignore cache needs original value not the alias
                  showCaptionOnLesson: !properties.showSlideCaptionOnLesson,
                },
                typename: 'ActivitiesSlideshowComponent',
              });
            }}
          />
        </SettingsGroup>

        {sortedSlides.length === 0 ? (
          <PStyled>No slides to display</PStyled>
        ) : (
          <PStyled>
            Drag and drop images below to change the order they appear in the
            slideshow.
          </PStyled>
        )}
        <SettingsGroup>
          <Sortable<SlideshowSlide>
            list={sortedSlides}
            renderItem={(item, index) => {
              const slide = sortedSlides.find(s => s.id === item.id);
              return (
                <SortableItem>
                  <SortableHeader>
                    <SecondaryButtonIcon
                      data-selector="icon-delete"
                      type="button"
                      $isSmall
                      disabled={deleteLoading && deleteItem === item.id}
                      onClick={() => {
                        setDeleteItem(item.id);
                        deleteComponent({
                          sectionId,
                          componentId: item.id,
                        }).then(() => {
                          setDeleteItem(undefined);
                          removeImageFromSlideShowInCache({
                            client,
                            slideshowComponentId: id,
                            imageComponentId: item.id,
                          });
                        });
                      }}
                    >
                      {deleteLoading && deleteItem === item.id ? (
                        <Spinner />
                      ) : (
                        <IconTrash width={20} height={20} />
                      )}
                    </SecondaryButtonIcon>
                  </SortableHeader>
                  <SortableContent>
                    {slide && (
                      <ComponentImageField
                        contentsOnly
                        image={{
                          fileName: slide.properties.image?.fileName || '',
                          key: slide.properties.image?.key || '',
                          alt: slide.properties.altText,
                          flip: slide.properties.image?.data?.flip,
                          original: slide.properties.image?.original,
                        }}
                        ariaLabel={`Edit image ${index + 1}`}
                        onEdit={() => {
                          setEditItemId(slide.id);
                          openModal();
                        }}
                      />
                    )}
                  </SortableContent>
                </SortableItem>
              );
            }}
            onSortChange={({ sourceIndex, destinationIndex, direction }) => {
              const slide = sortedSlides[sourceIndex];
              if (sourceIndex !== destinationIndex) {
                const newSort = getReorderSort<SlideshowSlide>(
                  sortedSlides,
                  destinationIndex,
                  direction
                );
                updateComponentProperties({
                  input: {
                    sectionId,
                    componentId: slide.id,
                    componentData: {
                      sort: newSort,
                    },
                  },
                  properties,
                  typename: 'ActivitiesImageComponent',
                });
              }
            }}
          />
        </SettingsGroup>
      </ActivityBuilderDrawerContent>
    </ActivityBuilderDrawerWrapper>
  );
};

export default SlideshowSettings;
